import { useEffect } from 'react';

interface UseDialogFocusArgs {
  dialogRef: React.RefObject<HTMLDivElement>;
}

/**
 * Hook which traps keyboard navigation in the dialog when it is open
 * inspired by: https://uxdesign.cc/how-to-trap-focus-inside-modal-to-make-it-ada-compliant-6a50f9a70700
 * @param {React.RefObject} dialogRef ref instance of an element
 */
const useDialogFocus = ({ dialogRef }: UseDialogFocusArgs) => {
  useEffect(() => {
    // if dialog ref wasn't defined
    if (!dialogRef.current) {
      return;
    }

    // query all focusable elements;
    const loadFocusableElements = () => {
      const focusableElements = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
      return dialogRef.current?.querySelectorAll(focusableElements) || [];
    };

    const focusHandler = (event: KeyboardEvent) => {
      // Ignore all key events that are not Tab
      if (event.key !== 'Tab') {
        return;
      }

      const focusableContent = loadFocusableElements();
      const firstFocusableElement = focusableContent[0];
      const lastFocusableElement = focusableContent[focusableContent.length - 1];

      // if shift key pressed for shift + tab combination
      if (event.shiftKey) {
        if (document.activeElement === firstFocusableElement) {
          // add focus for the last focusable element
          (lastFocusableElement as HTMLElement).focus();
          event.preventDefault();
        }
      } else {
        // if focused has reached to last focusable element then focus first focusable element after pressing tab
        if (document.activeElement === lastFocusableElement) {
          // add focus for the first focusable element
          (firstFocusableElement as HTMLElement).focus();
          event.preventDefault();
        }
      }
    };

    // focus on first element on mount
    const firstFocusableElement = loadFocusableElements()[0];
    (firstFocusableElement as HTMLElement)?.focus();

    // load events
    document.addEventListener('keydown', focusHandler);

    // eslint-disable-next-line consistent-return
    return () => {
      // unload events
      document.removeEventListener('keydown', focusHandler);
    };
  }, [dialogRef]);
};

export default useDialogFocus;
